/**
 * Zdroje jízdních řádů MHD:
 *  - GTFS import (reálná data ze zip souboru dopravce / open dat),
 *  - ruční síť (zastávky klikem do mapy, linky s intervalem či odjezdy),
 *  - demo síť (vždy výslovná volba, transparentně označená jako odhad),
 *  - vypnuto (MHD se neplánuje, UI vysvětlí proč).
 */
import { importGtfsBytes, importGtfsCz } from '../data/gtfsImport';
import { czEtag, CZ_ATTRIBUTION } from '../data/gtfsCz';
import { bboxAround, type GtfsBbox } from '../data/gtfsLarge';
import {
  FEED_CATALOG,
  fetchCatalogFeed,
  fetchGtfsZip,
  formatBytes,
  type FeedDef,
  type FeedValidators,
} from '../data/feeds';
import { haversineKm } from '../core/geo';
import { createManualLine } from '../core/transit';
import { parseHHMM, pragueParts } from '../core/time';
import type { TransitLine, TransitNetwork, TransitStop, TransitVehicle } from '../core/types';
import type { Store, TransitSource } from '../state/store';
import { openDialog } from './dialogs';
import { el, clear } from './dom';
import { infoDot } from './help';
import { icon } from './icons';

export interface TransitEditorCallbacks {
  onChanged(): void;
  /** Požádá mapu o výběr polohy zastávky kliknutím. */
  requestStopPick(cb: (lat: number, lng: number) => void): void;
}

const SOURCE_INFO: Array<{ id: TransitSource; title: string; desc: string }> = [
  { id: 'gtfs', title: 'GTFS import', desc: 'Reálné jízdní řády ze zip souboru (open data dopravců).' },
  { id: 'manual', title: 'Ruční síť', desc: 'Vlastní zastávky a linky — reálné údaje, které znáte.' },
  { id: 'demo', title: 'Demo síť', desc: 'Modelová síť kolem trasy. Jen ukázka — vždy označená „odhad".' },
  { id: 'none', title: 'Vypnuto', desc: 'MHD se nebude plánovat, dokud nevyberete zdroj.' },
];

export function openTransitEditor(store: Store, cb: TransitEditorCallbacks): void {
  // Ruční síť editujeme nad kopií; uložíme až tlačítkem.
  const existing = store.get().manualTransit;
  const net: TransitNetwork = existing
    ? JSON.parse(JSON.stringify(existing))
    : { stops: [], lines: [], maxAccessKm: 1.2, transferMin: 3, source: 'manual' as const };

  let stopSeq = net.stops.length + 1;

  /* ---------------- výběr zdroje ---------------- */
  const sourceBox = el('div', { className: 'source-grid', role: 'radiogroup', 'aria-label': 'Zdroj jízdních řádů' });
  const renderSources = (): void => {
    clear(sourceBox);
    const current = store.get().transitSource;
    for (const s of SOURCE_INFO) {
      const isGtfsWithoutData = s.id === 'gtfs' && !store.get().gtfsTransit;
      const isManualWithoutData = s.id === 'manual' && !store.get().manualTransit;
      sourceBox.append(
        el(
          'button',
          {
            className: `source-card${current === s.id ? ' source-card--on' : ''}`,
            type: 'button',
            role: 'radio',
            'aria-checked': String(current === s.id),
            onclick: () => {
              if (isGtfsWithoutData) {
                gtfsFileInput.click();
                return;
              }
              if (isManualWithoutData && net.stops.length === 0) {
                // přepneme až po uložení ruční sítě; zatím jen scroll na editor
                manualSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
              }
              store.update({ transitSource: s.id, selectedItineraryId: null });
              cb.onChanged();
              renderSources();
              renderGtfsStatus();
            },
          },
          el('strong', {}, s.title),
          el('span', { className: 'muted' }, s.desc),
          s.id === 'gtfs' && store.get().gtfsFeedName
            ? el('span', { className: 'badge badge--real' }, store.get().gtfsFeedName!)
            : null,
        ),
      );
    }
  };

  /* ---------------- GTFS import: katalog, URL, soubor ---------------- */
  const gtfsFileInput = el('input', { type: 'file', accept: '.zip,application/zip', style: 'display:none' }) as HTMLInputElement;
  const gtfsStatus = el('div', { role: 'status' });
  let importing = false;

  const renderGtfsStatus = (): void => {
    clear(gtfsStatus);
    const s = store.get();
    if (s.gtfsTransit) {
      gtfsStatus.append(
        el(
          'div',
          { className: 'muted', style: 'margin-top:6px' },
          `Načteno: ${s.gtfsFeedName ?? 'GTFS feed'} · ${s.gtfsTransit.stops.length} zastávek · ${s.gtfsTransit.lines.length} linek.`,
        ),
      );
    }
  };

  const showProgress = (text: string): void => {
    clear(gtfsStatus);
    gtfsStatus.append(el('div', { className: 'muted', role: 'status' }, text));
  };

  /** Pokrývá meta.bbox aktuální body A/B? (jinak je potřeba nový ořez) */
  const bboxCoversRoute = (bbox: GtfsBbox | null): boolean => {
    const s = store.get();
    if (!bbox) return true; // celý feed
    const inside = (p: { lat: number; lng: number } | null): boolean =>
      !p || (p.lat >= bbox.south && p.lat <= bbox.north && p.lng >= bbox.west && p.lng <= bbox.east);
    return inside(s.origin) && inside(s.destination);
  };

  /**
   * Společný import: parsování běží ve web workeru (UI se neseká);
   * u velkých feedů ořez na okolí trasy (streaming v workeru).
   */
  const importBytes = async (
    bytes: Uint8Array,
    sourceName: string,
    origin: { sourceId: string | null; url: string | null; validators: FeedValidators | null },
  ): Promise<void> => {
    const s = store.get();
    const p = pragueParts(Date.now());
    const pad = (n: number): string => String(n).padStart(2, '0');
    const serviceDate = `${p.year}-${pad(p.month)}-${pad(p.day)}`;

    let bbox: GtfsBbox | null = null;
    if (s.origin && s.destination) {
      const padKm = Math.max(20, haversineKm(s.origin, s.destination) * 0.5);
      bbox = bboxAround([s.origin, s.destination], padKm);
      showProgress('Zpracovávám jízdní řády na pozadí (ořez na okolí trasy)…');
    } else if (bytes.length > 15 * 1024 * 1024) {
      throw new Error(
        'Feed je velký — nejdřív zadejte body A a B, ať můžu síť oříznout na okolí vaší trasy.',
      );
    } else {
      showProgress('Zpracovávám jízdní řády na pozadí…');
    }

    const result = await importGtfsBytes(bytes, { bbox, serviceDate });

    store.update({
      gtfsTransit: result.network,
      gtfsFeedName: result.feedName ?? sourceName,
      gtfsMeta: {
        sourceId: origin.sourceId,
        url: origin.url,
        etag: origin.validators?.etag ?? null,
        lastModified: origin.validators?.lastModified ?? null,
        downloadedAt: new Date().toISOString(),
        bbox,
      },
      transitSource: 'gtfs',
      selectedItineraryId: null,
    });
    cb.onChanged();
    clear(gtfsStatus);
    gtfsStatus.append(
      el(
        'div',
        { className: 'alert alert--info' },
        `Importováno: ${result.stats.stops} zastávek, ${result.stats.lines} linek (${result.stats.trips} spojů).`,
      ),
    );
    for (const w of result.warnings.slice(0, 4)) {
      gtfsStatus.append(el('div', { className: 'muted' }, `· ${w}`));
    }
    renderSources();
    renderCatalog();
  };

  const failImport = (err: unknown): void => {
    clear(gtfsStatus);
    gtfsStatus.append(
      el('div', { className: 'alert alert--warn', role: 'alert' }, err instanceof Error ? err.message : 'GTFS se nepodařilo načíst.'),
    );
  };

  const progressOf = (sourceName: string) => (pr: { loadedBytes: number; totalBytes: number | null }) =>
    showProgress(
      `Stahuji ${sourceName}… ${formatBytes(pr.loadedBytes)}${pr.totalBytes ? ` / ${formatBytes(pr.totalBytes)}` : ''}`,
    );

  const setImporting = (on: boolean): void => {
    importing = on;
    renderCatalog();
  };

  const importFromUrl = async (url: string, sourceName: string): Promise<void> => {
    if (importing) return;
    setImporting(true);
    try {
      const s = store.get();
      const meta = s.gtfsMeta;
      // Stejná URL + data v aplikaci + pokrytá trasa → jen aktualizace při změně.
      const conditional =
        s.gtfsTransit && meta?.url === url && bboxCoversRoute(meta.bbox)
          ? { etag: meta.etag, lastModified: meta.lastModified }
          : null;
      const res = await fetchGtfsZip(url, progressOf(sourceName), conditional);
      if (res.notModified) {
        clear(gtfsStatus);
        gtfsStatus.append(
          el('div', { className: 'alert alert--info' }, 'Jízdní řády jsou aktuální — soubor se na serveru nezměnil, nic se nestahovalo.'),
        );
        return;
      }
      await importBytes(res.bytes, sourceName, { sourceId: null, url, validators: res.validators });
    } catch (err) {
      failImport(err);
    } finally {
      setImporting(false);
    }
  };

  /** Celá ČR: agregovaný celostátní dataset (stream + ořez ve workeru). */
  const importCz = async (): Promise<void> => {
    if (importing) return;
    const s0 = store.get();
    if (!s0.origin || !s0.destination) {
      failImport(new Error('Celostátní dataset je velký — nejdřív zadejte body A a B, ať síť ořežu na okolí trasy.'));
      return;
    }
    setImporting(true);
    try {
      const meta = s0.gtfsMeta;
      // „Aktualizovat": přes ETag datasetu — beze změny se nic nestahuje.
      if (s0.gtfsTransit && meta?.url === 'cz-merged' && bboxCoversRoute(meta.bbox)) {
        showProgress('Ověřuji aktuálnost celostátního datasetu…');
        const tag = await czEtag();
        if (tag && meta.etag && tag === meta.etag) {
          clear(gtfsStatus);
          gtfsStatus.append(el('div', { className: 'alert alert--info' }, 'Celá ČR: jízdní řády jsou aktuální — dataset se nezměnil, nic se nestahovalo.'));
          return;
        }
      }
      const p = pragueParts(Date.now());
      const pad = (n: number): string => String(n).padStart(2, '0');
      const padKm = Math.max(20, haversineKm(s0.origin, s0.destination) * 0.5);
      const bbox = bboxAround([s0.origin, s0.destination], padKm);
      const result = await importGtfsCz(bbox, `${p.year}-${pad(p.month)}-${pad(p.day)}`, (t) => showProgress(t));
      const tag = await czEtag();
      store.update({
        gtfsTransit: result.network,
        gtfsFeedName: 'Celá ČR',
        gtfsMeta: { sourceId: 'cz', url: 'cz-merged', etag: tag, lastModified: null, downloadedAt: new Date().toISOString(), bbox },
        transitSource: 'gtfs',
        selectedItineraryId: null,
      });
      cb.onChanged();
      clear(gtfsStatus);
      gtfsStatus.append(
        el('div', { className: 'alert alert--info' }, `Importováno z celostátního datasetu: ${result.stats.stops} zastávek, ${result.stats.lines} linek (${result.stats.trips} spojů).`),
      );
      for (const w of result.warnings.slice(0, 3)) gtfsStatus.append(el('div', { className: 'muted' }, `· ${w}`));
      renderSources();
      renderCatalog();
    } catch (err) {
      failImport(err);
    } finally {
      setImporting(false);
    }
  };

  const importFromCatalog = async (feed: FeedDef): Promise<void> => {
    if (importing) return;
    setImporting(true);
    try {
      const s = store.get();
      const meta = s.gtfsMeta;
      const sameSource = Boolean(s.gtfsTransit && meta?.sourceId === feed.id);
      const covers = sameSource && bboxCoversRoute(meta?.bbox ?? null);
      // Už staženo a oblast sedí → podmíněný požadavek (stáhne se jen změna).
      const conditional = sameSource && covers ? { etag: meta!.etag, lastModified: meta!.lastModified } : null;
      if (sameSource && !covers) {
        showProgress('Trasa je mimo dříve importovanou oblast — stahuji feed pro nový ořez…');
      }
      const res = await fetchCatalogFeed(feed, progressOf(feed.name), conditional);
      if (res.notModified) {
        clear(gtfsStatus);
        gtfsStatus.append(
          el('div', { className: 'alert alert--info' }, `${feed.name}: jízdní řády jsou aktuální — soubor se na serveru nezměnil, nic se nestahovalo.`),
        );
        return;
      }
      await importBytes(res.bytes, feed.name, { sourceId: feed.id, url: feed.proxyPath, validators: res.validators });
    } catch (err) {
      failImport(err);
    } finally {
      setImporting(false);
    }
  };

  gtfsFileInput.addEventListener('change', async () => {
    const file = gtfsFileInput.files?.[0];
    if (!file || importing) return;
    setImporting(true);
    try {
      showProgress('Načítám soubor…');
      const bytes = new Uint8Array(await file.arrayBuffer());
      await importBytes(bytes, file.name.replace(/\.zip$/i, ''), { sourceId: null, url: null, validators: null });
    } catch (err) {
      failImport(err);
    } finally {
      setImporting(false);
      gtfsFileInput.value = '';
    }
  });

  // Katalog ověřených feedů + import z libovolné URL. Tlačítka se mění:
  // „Stáhnout" (ještě nestaženo) × „Aktualizovat" (stáhne se jen při změně).
  const urlInput = el('input', {
    className: 'input',
    type: 'url',
    placeholder: 'https://… (URL GTFS zipu kteréhokoli dopravce)',
    'aria-label': 'URL GTFS zipu',
  }) as HTMLInputElement;
  const catalogRow = el('div', { className: 'row row--wrap' });
  const renderCatalog = (): void => {
    clear(catalogRow);
    const s = store.get();
    // Celá ČR — agregovaný celostátní dataset (215 dopravců, MHD + regionální).
    {
      const isCz = Boolean(s.gtfsTransit && s.gtfsMeta?.sourceId === 'cz');
      const covers = !isCz || bboxCoversRoute(s.gtfsMeta?.bbox ?? null);
      const label = isCz ? (covers ? 'Aktualizovat Celá ČR' : 'Stáhnout pro novou oblast: Celá ČR') : 'Stáhnout Celá ČR';
      catalogRow.append(
        el(
          'button',
          {
            className: 'btn btn--small btn--primary',
            disabled: importing ? 'true' : undefined,
            title: `Všechny veřejné jízdní řády ČR (215 dopravců, MHD i regionální linky), ořezané na okolí vaší trasy. ${CZ_ATTRIBUTION}`,
            onclick: () => void importCz(),
          },
          icon(isCz && covers ? 'check' : 'download', { size: 13 }),
          ` ${label}`,
        ),
      );
    }
    for (const f of FEED_CATALOG) {
      const isCurrent = Boolean(s.gtfsTransit && s.gtfsMeta?.sourceId === f.id);
      const covers = !isCurrent || bboxCoversRoute(s.gtfsMeta?.bbox ?? null);
      const label = isCurrent ? (covers ? `Aktualizovat ${f.name}` : `Stáhnout pro novou oblast: ${f.name}`) : `Stáhnout ${f.name}`;
      catalogRow.append(
        el(
          'button',
          {
            className: 'btn btn--small',
            disabled: importing ? 'true' : undefined,
            title: isCurrent && covers ? `${f.note} Stáhne se jen, pokud se soubor na serveru změnil.` : f.note,
            onclick: () => void importFromCatalog(f),
          },
          icon(isCurrent && covers ? 'check' : 'download', { size: 13 }),
          ` ${label}`,
        ),
      );
    }
    catalogRow.append(
      el(
        'button',
        { className: 'btn btn--small', disabled: importing ? 'true' : undefined, onclick: () => gtfsFileInput.click() },
        icon('upload', { size: 13 }),
        ' Nahrát zip',
      ),
    );
  };
  renderCatalog();

  const gtfsCatalog = el(
    'div',
    { style: 'margin-top:8px' },
    catalogRow,
    el(
      'div',
      { className: 'row', style: 'margin-top:6px' },
      el('div', { className: 'grow' }, urlInput),
      el(
        'button',
        {
          className: 'btn btn--small',
          onclick: () => {
            const u = urlInput.value.trim();
            if (u) void importFromUrl(u, new URL(u, window.location.href).hostname);
          },
        },
        'Importovat z URL',
      ),
    ),
    el(
      'div',
      { className: 'hint' },
      '„Celá ČR" pokrývá všechny veřejné jízdní řády republiky (215 dopravců, MHD i regionální) a ořízne je na okolí vaší trasy — zadejte proto nejdřív A a B. Stažená data se znovu nestahují — „Aktualizovat" přenese jen změny. Konkrétního dopravce lze přidat i přes URL jeho GTFS.',
    ),
  );

  /* ---------------- ruční síť (stávající editor) ---------------- */
  const stopsBox = el('div', {});
  const linesBox = el('div', { style: 'display:flex;flex-direction:column;gap:10px' });

  const renderStops = (): void => {
    clear(stopsBox);
    if (net.stops.length === 0) {
      stopsBox.append(el('div', { className: 'muted' }, 'Zatím žádné zastávky. Přidejte je kliknutím do mapy.'));
    }
    for (const s of net.stops) {
      const nameInput = el('input', {
        className: 'input',
        value: s.name,
        'aria-label': 'Název zastávky',
        onchange: (e: Event) => {
          s.name = (e.target as HTMLInputElement).value.trim() || s.name;
          renderLines();
        },
      });
      stopsBox.append(
        el(
          'div',
          { className: 'row', style: 'margin-bottom:6px' },
          el('span', { className: 'stop-dot', 'aria-hidden': 'true', style: 'flex-shrink:0' }),
          el('div', { className: 'grow' }, nameInput),
          el('span', { className: 'muted' }, `${s.lat.toFixed(4)}, ${s.lng.toFixed(4)}`),
          el(
            'button',
            {
              className: 'btn btn--small btn--ghost btn--icon',
              'aria-label': `Smazat zastávku ${s.name}`,
              onclick: () => {
                net.stops = net.stops.filter((x) => x.id !== s.id);
                for (const l of net.lines) l.stopIds = l.stopIds.filter((id) => id !== s.id);
                renderStops();
                renderLines();
              },
            },
            icon('trash'),
          ),
        ),
      );
    }
  };

  const VEHICLE_ORDER: Array<{ id: TransitVehicle; name: string }> = [
    { id: 'metro', name: 'Metro' },
    { id: 'tram', name: 'Tramvaje' },
    { id: 'train', name: 'Vlaky' },
    { id: 'bus', name: 'Autobusy' },
    { id: 'ferry', name: 'Přívozy' },
  ];

  const renderLines = (): void => {
    clear(linesBox);
    if (net.lines.length === 0) {
      linesBox.append(el('div', { className: 'muted' }, 'Zatím žádné linky.'));
    }
    // Linky seskupené podle druhu vozidla.
    for (const v of VEHICLE_ORDER) {
      const group = net.lines.filter((l) => l.vehicle === v.id);
      if (group.length === 0) continue;
      linesBox.append(el('div', { className: 'mode-group__head' }, el('span', { className: 'mode-group__name' }, `${v.name} (${group.length})`)));
      for (const line of group) linesBox.append(renderLineCard(line));
    }
  };

  const renderLineCard = (line: TransitLine): HTMLElement => {
    const nameI = el('input', { className: 'input', value: line.name, onchange: (e: Event) => (line.name = (e.target as HTMLInputElement).value) });
    const vehicleS = el(
      'select',
      { className: 'select', onchange: (e: Event) => (line.vehicle = (e.target as HTMLSelectElement).value as TransitVehicle) },
      opt('bus', 'Autobus', line.vehicle),
      opt('tram', 'Tramvaj', line.vehicle),
      opt('metro', 'Metro', line.vehicle),
      opt('train', 'Vlak', line.vehicle),
      opt('ferry', 'Přívoz', line.vehicle),
    );
    const colorI = el('input', { type: 'color', className: 'input', style: 'height:36px;padding:3px', value: line.color, onchange: (e: Event) => (line.color = (e.target as HTMLInputElement).value) });
    const headwayI = numInput(line.headwayMin ?? 10, (v) => (line.headwayMin = v), 1, 240);
    const firstI = timeInput(line.firstDep ?? '05:00', (v) => (line.firstDep = v));
    const lastI = timeInput(line.lastDep ?? '23:00', (v) => (line.lastDep = v));
    const depsI = el('input', {
      className: 'input',
      placeholder: 'např. 7:15, 8:40, 12:05 (přepíše interval)',
      value: (line.departures ?? []).join(', '),
      onchange: (e: Event) => {
        const raw = (e.target as HTMLInputElement).value.trim();
        if (!raw) {
          line.departures = undefined;
          return;
        }
        const parsed = raw
          .split(/[,;\s]+/)
          .map((t) => t.trim())
          .filter(Boolean)
          .filter((t) => parseHHMM(t) !== null);
        line.departures = parsed.length > 0 ? parsed : undefined;
        (e.target as HTMLInputElement).value = (line.departures ?? []).join(', ');
      },
    });
    const speedI = numInput(line.speedKmh, (v) => (line.speedKmh = v), 3, 300);
    const costI = numInput(line.costFlat, (v) => (line.costFlat = v), 0, 5000);
    const accI = el('input', { type: 'checkbox', checked: line.accessible, onchange: (e: Event) => (line.accessible = (e.target as HTMLInputElement).checked) });
    const bidiI = el('input', { type: 'checkbox', checked: line.bidirectional, onchange: (e: Event) => (line.bidirectional = (e.target as HTMLInputElement).checked) });

    const seqBox = el('div', { className: 'row row--wrap', style: 'gap:5px' });
    const renderSeq = (): void => {
      clear(seqBox);
      line.stopIds.forEach((id, idx) => {
        const stop = net.stops.find((s) => s.id === id);
        if (!stop) return;
        seqBox.append(
          el(
            'span',
            { className: 'stop-chip' },
            `${idx + 1}. ${stop.name}`,
            el('button', { 'aria-label': `Odebrat ${stop.name} z linky`, onclick: () => { line.stopIds.splice(idx, 1); renderSeq(); } }, '✕'),
          ),
        );
      });
      const addSelect = el(
        'select',
        { className: 'select', style: 'width:auto', 'aria-label': 'Přidat zastávku do linky' },
        el('option', { value: '' }, '+ přidat zastávku…'),
        ...net.stops
          .filter((s) => !line.stopIds.includes(s.id))
          .map((s) => el('option', { value: s.id }, s.name)),
      ) as HTMLSelectElement;
      addSelect.addEventListener('change', () => {
        if (addSelect.value) {
          line.stopIds.push(addSelect.value);
          renderSeq();
        }
      });
      seqBox.append(addSelect);
    };
    renderSeq();

    return el(
      'div',
      { className: 'line-card' },
      el(
        'div',
        { className: 'row' },
        el('strong', { style: 'flex:1' }, `Linka ${line.name}`),
        el(
          'button',
          {
            className: 'btn btn--small btn--ghost',
            'aria-label': `Smazat linku ${line.name}`,
            onclick: () => {
              net.lines = net.lines.filter((l) => l.id !== line.id);
              renderLines();
            },
          },
          icon('trash'),
          ' Smazat',
        ),
      ),
      el(
        'div',
        { className: 'field-grid field-grid--3' },
        labeled('Označení', nameI),
        labeled('Druh', vehicleS),
        labeled('Barva', colorI),
        labeled('Interval (min)', headwayI),
        labeled('První spoj', firstI),
        labeled('Poslední spoj', lastI),
        labeled('Rychlost (km/h)', speedI),
        labeled('Jízdné (Kč)', costI),
        el('div', { className: 'row', style: 'align-items:flex-end;padding-bottom:6px' },
          el('label', { className: 'switch' }, accI, el('span', {}, 'Bezbariérová')),
        ),
      ),
      labeled('Konkrétní odjezdy z první zastávky (volitelné)', depsI),
      el('label', { className: 'switch' }, bidiI, el('span', {}, 'Jezdí oběma směry')),
      el('div', {}, el('label', { className: 'label' }, 'Pořadí zastávek'), seqBox),
      line.stopIds.length < 2 ? el('div', { className: 'alert alert--warn' }, 'Linka potřebuje alespoň 2 zastávky.') : null,
    );
  };

  renderSources();
  renderGtfsStatus();
  renderStops();
  renderLines();

  const manualSection = el(
    'div',
    {},
    el(
      'div',
      { className: 'card__title', style: 'margin-top:14px' },
      'Ruční síť — zastávky',
      el('span', { className: 'spacer' }),
      el(
        'button',
        {
          className: 'btn btn--small',
          onclick: () => {
            cb.requestStopPick((lat, lng) => {
              net.stops.push({
                id: `stop-${Date.now().toString(36)}-${stopSeq}`,
                name: `Zastávka ${stopSeq++}`,
                lat,
                lng,
              } satisfies TransitStop);
              renderStops();
              renderLines();
            });
          },
        },
        icon('map-pin'),
        ' Přidat kliknutím do mapy',
      ),
    ),
    stopsBox,
    el(
      'div',
      { className: 'card__title', style: 'margin-top:12px' },
      'Ruční síť — linky',
      el('span', { className: 'spacer' }),
      el(
        'button',
        {
          className: 'btn btn--small',
          onclick: () => {
            net.lines.push(createManualLine());
            renderLines();
          },
        },
        icon('plus'),
        ' Přidat linku',
      ),
    ),
    linesBox,
  );

  const body = el(
    'div',
    {},
    el(
      'div',
      { className: 'card__title' },
      'Zdroj jízdních řádů',
      infoDot(
        store,
        'Odkud bere aplikace spoje MHD. GTFS = reálná open data dopravců (zip). Ruční síť = vaše vlastní linky. Demo je jen modelová ukázka — výsledky s ní jsou vždy označené „odhad".',
      ),
    ),
    sourceBox,
    gtfsCatalog,
    gtfsFileInput,
    gtfsStatus,
    manualSection,
  );

  const dlg = openDialog({
    title: 'Jízdní řády MHD',
    body,
    wide: true,
    footer: [
      el('span', { style: 'margin-right:auto' }),
      el('button', { className: 'btn', onclick: () => dlg.close() }, 'Zavřít'),
      el(
        'button',
        {
          className: 'btn btn--primary',
          onclick: () => {
            const valid = {
              ...net,
              lines: net.lines.filter((l) => l.stopIds.length >= 2),
              source: 'manual' as const,
            };
            if (valid.stops.length === 0 || valid.lines.length === 0) {
              store.update({
                manualTransit: null,
                transitSource: store.get().transitSource === 'manual' ? 'none' : store.get().transitSource,
              });
            } else {
              store.update({ manualTransit: valid, transitSource: 'manual' });
            }
            cb.onChanged();
            dlg.close();
          },
        },
        'Uložit ruční síť',
      ),
    ],
  });

  function labeled(label: string, input: HTMLElement): HTMLElement {
    return el('div', {}, el('label', { className: 'label' }, label), input);
  }

  function opt(value: string, label: string, current: string): HTMLElement {
    return el('option', { value, selected: value === current }, label);
  }

  function numInput(value: number, set: (v: number) => void, min: number, max: number): HTMLElement {
    return el('input', {
      className: 'input',
      type: 'number',
      value: String(value),
      min: String(min),
      max: String(max),
      onchange: (e: Event) => {
        const n = Number((e.target as HTMLInputElement).value);
        if (Number.isFinite(n)) set(Math.min(max, Math.max(min, n)));
      },
    });
  }

  function timeInput(value: string, set: (v: string) => void): HTMLElement {
    return el('input', {
      className: 'input',
      type: 'time',
      value,
      onchange: (e: Event) => {
        const v = (e.target as HTMLInputElement).value;
        if (parseHHMM(v) !== null) set(v);
      },
    });
  }
}
